import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'

/**
 * Represents the payload for a transaction category
 */
export type CategoryPayload = {
  /**
   * The category id
   */
  id: string
  /**
   * The organization id this category belongs to
   */
  organizationId: string
  /**
   * The category name
   */
  name: string
  /**
   * The category color used for label display purposes in the ui
   */
  color: string
  /**
   * Category type - USER (user-managed with full permissions) or SYSTEM (system-managed with restricted permissions)
   */
  type: CategoryType
  /**
   * The status of the category (e.g., ACTIVE, INACTIVE)
   */
  status: CategoryStatus
  /**
   * Determines which module this category belongs to (APA or OPERATIONS).
   * Uses AwxConnectedAccountType to associate with the appropriate module.
   */
  awxConnectedAccountType: AwxConnectedAccountType
  /**
   * The category icon, used for display purposes in the ui (can be null)
   */
  icon?: string
  /**
   * The category metadata, used for storing additional information about the category
   */
  metadata?: CategoryMetadata
  /**
   * awx connected account id this category belongs to. Org level category when this field is not set.
   */
  awxConnectedAccountId?: string
}

/**
 * Type used by the client to submit a category creation request.
 * `type` is optional for backward compatibility - defaults to APA on the backend.
 */
export type CategoryBody = {
  /**
   * The category name
   */
  name: string
  /**
   * The category color used for label display purposes in the ui
   */
  color: string
  /**
   * Determines which module this category belongs to (APA or OPERATIONS).
   * Optional - defaults to APA for backward compatibility with existing frontend.
   */
  awxConnectedAccountType?: AwxConnectedAccountType
  /**
   * The category icon, used for display purposes in the ui (can be undefined)
   */
  icon?: string
  /**
   * The status of the category (defaults to ACTIVE)
   */
  status?: CategoryStatus
  /**
   * The metadata of the category, used for storing additional information about the category
   */
  metadata?: CategoryMetadata
  /**
   * The awx connected account id this category belongs to. Org level category when this field is not set.
   */
  awxConnectedAccountId?: string
}

/**
 * Defines who manages a category and what operations are allowed:
 * - USER: Created and fully managed by users. Users can edit name, color, icon, and status (when implemented).
 * - SYSTEM: Managed by the system (e.g., imported from Xero). Users can only edit color and icon.
 *          Cannot change name or status. Not selectable when creating expenses.
 */
export enum CategoryType {
  /**
   * User-managed category with full edit permissions
   */
  USER = 'USER',
  /**
   * System-managed category with restricted edit permissions (color and icon only)
   */
  SYSTEM = 'SYSTEM',
  /**
   * Xero synced category, with restricted edit permissions (color and icon only)
   */
  XERO = 'XERO',
  /**
   * QuickBooks synced category, with restricted edit permissions (color and icon only)
   */
  QUICKBOOKS = 'QUICKBOOKS',
}

/**
 * Indicates whether a category is currently active and visible, or inactive (hidden, disabled).
 * - ACTIVE: Category is usable (visible in UI, selectable for transactions).
 * - INACTIVE: Category is disabled (not shown or selectable, but preserved for record/history).
 */
export enum CategoryStatus {
  /**
   * Category is usable (visible in UI, selectable for transactions).
   */
  ACTIVE = 'ACTIVE',
  /**
   * Category is disabled (not shown or selectable, but preserved for record/history).
   */
  INACTIVE = 'INACTIVE',
}

/**
 * Arguments for listing categories with pagination and filtering
 */
export type ListCategoriesArgs = {
  /**
   * Number of records to return (maximum 1000)
   */
  take?: number
  /**
   * Number of records to skip for pagination
   */
  skip?: number
  /**
   * Optional filter for connected account types (APA or OPERATIONS module).
   * If not provided, returns categories for all connected account types.
   */
  awxConnectedAccountTypes?: AwxConnectedAccountType[]
  /**
   * Optional filter for category types (USER or SYSTEM).
   * If not provided, returns all category types.
   */
  categoryTypes?: CategoryType[]
  /**
   * Optional filter for category statuses (ACTIVE or INACTIVE).
   * If not provided, returns all category statuses.
   */
  statuses?: CategoryStatus[]
  /**
   * Optional filter for awx connected account id.
   * If not provided, returns categories for all connected account ids.
   */
  awxConnectedAccountId?: string
}

/**
 * Type used by the client to submit a category update request.
 * Excludes `type` and `awxConnectedAccountType` as these fields cannot be changed after creation.
 */
export type UpdateCategoryBody = Partial<Omit<CategoryBody, 'type' | 'awxConnectedAccountType'>> & {
  /**
   * The status of the category (optional)
   */
  status?: CategoryStatus
}

/**
 * Type used by the backend to represent a category update request
 */
export type UpdateCategoryRequest = UpdateCategoryBody & {
  /**
   * The category id
   */
  id: string
}

/**
 * Flips every category of one integration to one status, across an organization.
 *
 * Scoped by account type as well as category type so an operations-only toggle cannot reach the
 * APA module's categories.
 */
export type UpdateCategoriesStatusArgs = {
  organizationId: string
  awxConnectedAccountType: AwxConnectedAccountType
  categoryType: CategoryType
  status: CategoryStatus
}

/**
 * Type used by the backend for category creation
 */
export type CreateCategoryRequest = CategoryBody & {
  /**
   * The category type (USER only for manual creation)
   */
  type: CategoryType
  /**
   * The organization id this category belongs to
   */
  organizationId: string
  /**
   * The status of the category (defaults to ACTIVE)
   */
  status?: CategoryStatus
  /**
   * The metadata of the category, used for storing additional information about the category
   */
  metadata?: CategoryMetadata
  /**
   * The awx connected account id this category belongs to. Org level category when this field is not set.
   */
  awxConnectedAccountId?: string
}

export type CategoryMetadata = XeroCategoryMetadata | QuickBooksCategoryMetadata

export type XeroCategoryMetadata = {
  account: {
    id: string
    code: string
    name: string
    description?: string
  }
}

export type QuickBooksCategoryMetadata = {
  account: QuickBooksCategoryAccountMetadata
}

export type QuickBooksCategoryAccountMetadata = {
  id: string
  code?: string
  name: string
  description?: string
  accountType?: string
  accountSubType?: string
}

/**
 * Type used in the backend to represent a category
 */
export type Category = CategoryPayload

/**
 * Backend domain type — represents a persisted `CategoryEnablement` row.
 * `state` is always one of the row-state subset of `CategoryEnablementState`
 * (`ENABLED` or `DISABLED`); the other enum values are synthesised at the
 * service/wire layer and must never be persisted.
 */
export type CategoryEnablement = {
  categoryId: string
  awxConnectedAccountId: string
  state: CategoryEnablementState
  createdAt?: string
  updatedAt?: string
}

export type CategoryAndEnablements = Category & {
  categoryEnablements: CategoryEnablement[]
}

export type ListCategoriesAndEnablementsArgs = {
  /**
   * The module the categories and enablements should belong to (APA or OPS).
   */
  awxConnectedAccountType: AwxConnectedAccountType
  /**
   * The category types to return (USER, SYSTEM, XERO).
   */
  categoryTypes?: CategoryType[]
  statuses?: CategoryStatus[]
  /** Limit the connected accounts to a specific set of ids (omit to get all accounts of matching type) */
  forConnectedAccountIds?: string[]
  take?: number
  skip?: number
}

/**
 * `ENABLED` and `DISABLED` are **row states** — they are valid values for
 * `CategoryEnablement.state` (the persisted column) and for the resolved wire
 * payload alike.
 *
 * `DEFAULT_ENABLED` and `LOCKED_ENABLED` are **wire-only states** synthesised by
 * the service layer and must never appear on a persisted row:
 *  - `DEFAULT_ENABLED` is emitted when no row exists for a (category, account) pair
 *    (absence-as-enabled).
 *  - `LOCKED_ENABLED` is emitted for SYSTEM/XERO categories which are always enabled
 *    and cannot be toggled.
 */
export enum CategoryEnablementState {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  DEFAULT_ENABLED = 'DEFAULT_ENABLED',
  LOCKED_ENABLED = 'LOCKED_ENABLED',
}

export type CategoryEnablementPayload = {
  categoryId: string
  awxConnectedAccountId: string
  state: CategoryEnablementState
}

/**
 * Wire-format payload representing a category, its coverage counts across the org's
 * connected accounts of the matching module, and its resolved per-account enablement state.
 */
export type CategoryWithEnablements = Category & {
  /** Number of connected accounts (of the matching module) where this category is currently enabled. */
  enabledConnectedAccountsCount: number
  /** Number of connected accounts in the org of the same type as the category (APA or OPS). */
  totalConnectedAccountsCount: number
  /**
   * Per-account enablement state for this category. Wire-format payloads (resolved state),
   * not raw `CategoryEnablement` rows.
   */
  accountEnablements: CategoryEnablementPayload[]
}
